// The sandbox screen's **written extent** (`client-sandbox-screen`, design **T4**, **D10**):
// how much of the selected run exists yet, how that splits into the three layers the scrub
// track paints, and whether a seek gesture is allowed to resolve at all.
//
// The reason this is a module rather than three expressions inside `PlaybackBar` is the
// second question. A batch trace **publishes atomically**: ROUNDS-MODE §5.3 makes the
// manifest the commit marker, so a generation directory without one is absent *by rule* and
// the shipped route answers `404 trace_absent` for a still-running run. Reading the files
// early would bypass the commit marker, the component checksums and the index bounds
// validation — the plausible-but-false-replay case **D-121** exists precisely to prevent it.
//
// So while a **server-executed** run is in flight the track is a **determinate progress bar
// and nothing more** (architecture review **A1**): the extent grows, and no seek resolves
// into it. On publication the whole trace lands and every tick becomes reachable. A readable
// in-flight prefix *over HTTP* is **D-182**, owned by `rounds-trace-http`, and deliberately
// not built here.
//
// ## The rule is split by where the run executes, not relaxed
//
// `rounds-practice-sandbox` MODIFIED that requirement and UI.md §4B carries the amendment:
// **the rule follows its reason rather than its appearance.** The reason above — "the bytes
// are not available to this client" — is *false* in exactly one case, and only in that one:
//
//   * A run executing **on the server** (every graded round, and every tournament sandbox run
//     until the successor change moves it) is unchanged. Its generation is across a process
//     boundary and reading it early would bypass the commit marker, the component checksums
//     and the index-bounds validation, which is the **D-121** class.
//   * A run executing **on this device** (`rounds-local-run`) has *this client* as its writer,
//     so the extent it has already written is readable **in-process** — no network read, no
//     commit marker crossed, no validation bypassed. The permission is a property of **owning
//     the writer**, not of the trace format or of the read protocol, so it transfers nothing to
//     the server reader and leaves **D-182** exactly where it was (design **D-D**).
//
// A seek **beyond** the written extent is still not honoured in either case: the target is
// reported **absent**, which is a normal result rather than an error.
//
// That is why {@link WrittenExtent}'s in-flight arm carries a **required** `site`. A default
// would have to be one of the two, and the permissive default is the dangerous one — it would
// let a reader resolve against an unpublished server generation, which is the whole failure this
// module exists to prevent. Making the caller state it means the D-121 direction cannot be
// reached by forgetting something.
//
// Three shapes deserve a note:
//
//   * **Refusal is a value carrying a reason, not a `false`.** The track has to *say* why a
//     drag did nothing; a boolean gate leaves the caller inventing the sentence, and the three
//     refusals mean genuinely different things ("wait a moment", "there is nothing here", and
//     "that moment has not happened yet").
//   * **An unknown span is `null`, never `0`.** `RunDto.terminalTick` is `null` while a run
//     is in flight, so the expected span comes from configuration the screen may not have. A
//     bar pinned at 0% reads as a stalled run — a fabricated value in exactly the sense the
//     screen's honesty rule forbids. `determinate: false` is the placeholder instead.
//   * **The hatched band is its own layer.** {@link TrackLayers.writing} is the span from the
//     playhead to the write head — written, not yet watched — so the playhead chasing the write
//     head reads as progress rather than as a stall (design **D-J**, sketch
//     `practice-framing.html` panel B). It is *not* derivable as "everything ahead of the
//     playhead": on a published run that span is simply unwatched, and hatching it would claim
//     a run was still being written after it had finished.
//
// Pure: no React, no DOM, no clock. The component renders these numbers; this module
// computes them.

import { fractionAtTick, tickAtFraction } from './review-model';

/**
 * Where a run's simulation executes — and therefore whether its written extent is readable by
 * this client at all.
 *
 * Not a rendering hint and not a mode flag: it is the *reason* the seek rule differs, named so the
 * rule can follow the reason. `'server'` is the shipped batch path (graded rounds and the
 * tournament sandbox); `'device'` is a practice run (`rounds-local-run`), where this client owns
 * the writer.
 */
export type RunExecutionSite = 'server' | 'device';

/**
 * What the screen currently knows about the selected run's extent.
 *
 * Three cases rather than a nullable record, because the seek rule differs in each and a
 * caller that has to reconstruct "is this in flight?" from `terminalTick === null` will
 * eventually get it wrong.
 */
export type WrittenExtent =
  /** No run selected, or a selection whose trace failed to load: the track is empty furniture. */
  | { readonly kind: 'absent' }
  /**
   * A run being simulated. `ticksObserved` is the progress count — `RunDto.ticksObserved` for a
   * server run, the device's own count for a local one; `expectedTerminalTick` is the run's
   * configured duration, `null` when the screen has no source for it (a server run does not
   * report one until it is terminal; a local run's run-start response always names it).
   *
   * `site` is **required** — see this file's header. There is no safe default.
   */
  | {
      readonly kind: 'in-flight';
      readonly site: RunExecutionSite;
      readonly ticksObserved: number;
      readonly expectedTerminalTick: number | null;
      /**
       * **The highest tick this client can actually draw** — the head of the watched prefix, which is
       * not the same thing as `ticksObserved`.
       *
       * A device run writes its whole season to disk but stops handing world state across the thread
       * boundary after `DEFAULT_WATCH_WINDOW_TICKS`. Past that the progress count keeps climbing while
       * there is nothing left to show, so a playhead or a drag bounded by `ticksObserved` travels into
       * a world that holds nothing — measured as the field emptying 3.8 s into a run at 16×, with
       * progress still reporting 08:55 of 30:00.
       *
       * So the two are separated by name: **this** bounds seeking and playhead advancement,
       * `ticksObserved` is left to the progress bar. One value, both consumers, so the track's
       * affordance and the gesture cannot disagree.
       *
       * `undefined` where the caller has nothing better to say — no watched prefix for *this* run —
       * and the seekable head then falls back to `ticksObserved`, which is the behaviour that predates
       * this field. The app's only producer (`writtenExtentFor`) supplies it whenever a watched world
       * belongs to the active run, so the fallback is reachable only for states where nothing is
       * readable anyway.
       */
      readonly watchedHeadTick?: number | null;
    }
  /** A published run whose trace is loaded. The whole span is written and readable. */
  | { readonly kind: 'published'; readonly firstTick: number; readonly terminalTick: number };

/** Why a seek did not resolve. */
export type SeekRefusalReason =
  /** The run's bytes are not readable by this client yet — a server run in flight, or a local one
   * whose horizon is not yet known and whose track is therefore indeterminate. */
  | 'run-in-flight'
  /** Nothing is loaded to scrub. */
  | 'no-trace-loaded'
  /** The run *is* readable, but this target is past the write head. **Absent, not an error.** */
  | 'beyond-written-extent';

/**
 * The sentence the track shows for each refusal. Phrased as a fact about the run, never as an
 * apology or an error: none of these is anything the player did wrong, and two of the three
 * resolve themselves within seconds.
 */
export const SEEK_REFUSAL_MESSAGES: Readonly<Record<SeekRefusalReason, string>> = {
  'run-in-flight':
    'This run is still being written — it becomes scrubbable the moment it finishes.',
  'no-trace-loaded': 'There is no run loaded to scrub. Press Run to simulate this version.',
  'beyond-written-extent': 'That moment has not been simulated yet — the run is still writing it.',
};

/** The outcome of a drag on the scrub track. */
export type SeekDecision =
  | { readonly kind: 'seek'; readonly tick: number }
  | {
      readonly kind: 'refused';
      readonly reason: SeekRefusalReason;
      readonly message: string;
    };

/** A span of the track, as 0..1 fractions of its full width. */
export interface TrackSpan {
  readonly from: number;
  readonly to: number;
}

/** The layers the track paints, as fractions of its full width. */
export interface TrackLayers {
  /**
   * Simulated so far — the coloured extent. `null` when the span is unknown, in which case the
   * track must take an indeterminate treatment rather than draw a proportion it cannot justify.
   */
  readonly written: number | null;
  /** The pale remainder: not yet happened. `null` for the same reason as {@link written}. */
  readonly unwritten: number | null;
  /** Already watched — the ink veil from the start of the track to the playhead. */
  readonly watched: number;
  /** `false` when the extent's span is unknown and the two fractions above are `null`. */
  readonly determinate: boolean;
  /**
   * The **hatched** band: written, ahead of the playhead, still being added to. `null` unless a run
   * is actually in flight with a known span and the write head is ahead of the playhead.
   *
   * This is what makes the written extent *distinguishable* rather than the gesture being silently
   * swallowed — required in both execution sites by UI.md §4B, for opposite reasons: where a seek is
   * refused it says why the drag did nothing, and where a seek is honoured it says how far the drag
   * may go.
   */
  readonly writing: TrackSpan | null;
}

/** A finite number, or the fallback — guards `NaN`/`Infinity` arriving from arithmetic upstream. */
function finiteOr(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}

/**
 * How much of the run has been written, as a 0..1 fraction of the track, or `null` when the
 * span is not knowable. Clamped at both ends: a server that reports more observed ticks than
 * the configured duration (a run that overran, or a stale expectation) fills the bar rather
 * than overflowing it.
 */
export function writtenFraction(extent: WrittenExtent): number | null {
  switch (extent.kind) {
    case 'absent':
      // Nothing has been simulated — a real, known zero, not an unknown.
      return 0;
    case 'published':
      // The manifest is the commit marker: if it is here, the whole run is here.
      return 1;
    case 'in-flight': {
      const span = extent.expectedTerminalTick;
      if (span === null || !Number.isFinite(span) || span <= 0) {
        return null;
      }
      const observed = Math.max(0, finiteOr(extent.ticksObserved, 0));
      return Math.min(1, observed / span);
    }
  }
}

/**
 * Where the playhead sits, as a 0..1 fraction. An `absent` extent and a `null` tick both pin
 * to the start; a published run maps through the same {@link fractionAtTick} the transport
 * clock uses, so the veil and the clock can never disagree.
 */
function watchedFraction(extent: WrittenExtent, playheadTick: number | null): number {
  if (playheadTick === null || extent.kind === 'absent') {
    return 0;
  }
  if (extent.kind === 'published') {
    return fractionAtTick(finiteOr(playheadTick, extent.firstTick), extent.firstTick, extent.terminalTick);
  }
  // In flight: the veil can never pass the written extent — it would depict the player having
  // watched a tick that does not exist yet. (For a *device* run the playhead is bounded tighter
  // still, by `watchedHeadTick`, so this clamp is satisfied with room to spare; it remains the
  // binding one for a server run, whose bytes are not this client's to read at all.)
  return Math.min(writtenFraction(extent) ?? 0, fractionAtTick(finiteOr(playheadTick, 0), 0, extent.expectedTerminalTick ?? 0));
}

/**
 * The track as its renderable layers. The component paints them; every rule about what may
 * be shown lives here, so a layer can never be drawn from a number the extent does not support.
 */
export function trackLayers(extent: WrittenExtent, playheadTick: number | null): TrackLayers {
  const written = writtenFraction(extent);
  const watched = watchedFraction(extent, playheadTick);
  return {
    written,
    unwritten: written === null ? null : 1 - written,
    watched,
    determinate: written !== null,
    writing:
      // Only a run genuinely in flight has a write head to chase. A published run's span ahead of
      // the playhead is unwatched, not unwritten, and hatching it would say the run was still
      // being written after it had finished.
      extent.kind === 'in-flight' && written !== null && written > watched
        ? { from: watched, to: written }
        : null,
  };
}

/**
 * The inclusive tick window a seek may resolve into, or `null` when this extent admits none.
 *
 * The one place the execution-site split is decided; {@link seekRefusalFor} and {@link decideSeek}
 * both read it, so the affordance, the gesture and the coordinator's re-check cannot drift apart.
 */
export function seekableWindow(extent: WrittenExtent): TrackSpan | null {
  switch (extent.kind) {
    case 'published':
      // The whole trace: an O(1) range read through the tick→offset index (ROUNDS-MODE §5.2).
      return { from: extent.firstTick, to: extent.terminalTick };
    case 'in-flight': {
      // **The split.** A server run's generation is not this client's to read at all.
      if (extent.site === 'server') {
        return null;
      }
      const span = extent.expectedTerminalTick;
      if (span === null || !Number.isFinite(span) || span <= 0) {
        // No horizon means an indeterminate track, and no fraction on an indeterminate track names
        // a tick. Refusing is honest; resolving against a span we have admitted we do not have is
        // how a plausible-but-wrong position gets drawn.
        return null;
      }
      // A local run's first record is tick 0, and the write head itself is reachable — inclusive,
      // because "the playhead chases the write head" is the behaviour being specified.
      //
      // **The head is what has crossed to this client, not what the writer has written.** Those part
      // company after `DEFAULT_WATCH_WINDOW_TICKS`: the run keeps writing and keeps reporting
      // progress, but no further world state is handed over, so `ticksObserved` names ticks nothing
      // here can draw. `watchedHeadTick` is the readable one; `ticksObserved` remains the fallback
      // for the states where the caller knows of no watched prefix for this run, which is the
      // behaviour that predates the field.
      const head = extent.watchedHeadTick ?? extent.ticksObserved;
      return { from: 0, to: Math.max(0, Math.min(span, finiteOr(head, 0))) };
    }
    case 'absent':
      return null;
  }
}

/**
 * Why a seek would be refused for this extent *as a whole*, or `null` when the track is draggable.
 *
 * Exported so the track's *affordance* (cursor, `tabIndex`, `aria-disabled`) reads the same rule
 * the gesture does. A track that looks draggable while refusing every drag is the failure this
 * one function prevents — and, since the practice track genuinely **is** draggable, so is its
 * mirror image.
 *
 * Note what this deliberately does **not** answer: whether one particular target resolves. A
 * device run in flight is draggable *and* refuses targets past its write head, which is
 * {@link decideSeek}'s to say per gesture.
 */
export function seekRefusalFor(extent: WrittenExtent): SeekRefusalReason | null {
  if (seekableWindow(extent) !== null) {
    return null;
  }
  return extent.kind === 'absent' ? 'no-trace-loaded' : 'run-in-flight';
}

/** The tick span the track *depicts*, which is not the span it admits a seek into. */
function trackSpanTicks(extent: WrittenExtent): TrackSpan | null {
  switch (extent.kind) {
    case 'published':
      return { from: extent.firstTick, to: extent.terminalTick };
    case 'in-flight': {
      const span = extent.expectedTerminalTick;
      return span === null || !Number.isFinite(span) || span <= 0 ? null : { from: 0, to: span };
    }
    case 'absent':
      return null;
  }
}

/**
 * Adjudicate a drag to `fraction` of the track's width.
 *
 * The fraction is read against the span the track **depicts** — the whole run — and the resulting
 * tick is then adjudicated against the span the extent **admits**. Those are the same thing for a
 * published run and deliberately different for a device run in flight, which is exactly why a drag
 * a fifth of the way along a 12%-written track is refused rather than clamped back to the write
 * head: clamping would silently move the playhead somewhere the player did not point.
 */
export function decideSeek(extent: WrittenExtent, fraction: number): SeekDecision {
  const depicted = trackSpanTicks(extent);
  if (depicted === null) {
    return refuse(seekRefusalFor(extent) ?? 'run-in-flight');
  }
  return decideSeekToTick(extent, tickAtFraction(fraction, depicted.from, depicted.to));
}

/**
 * Adjudicate a seek to an already-resolved `tick`.
 *
 * The coordinator's re-check of a gesture the track resolved: the same rule asked in the same
 * place, rather than a second copy of it that could disagree with the affordance the player saw.
 *
 * **Two different out-of-range answers, and the difference is the point.** A target outside the
 * span the track *depicts* is an out-of-track gesture and **clamps** — dragging past the right edge
 * means the end of the run, not a tick the trace lacks. A target inside the track but past the
 * **write head** is **absent** and refuses: clamping it back to the write head would silently move
 * the playhead somewhere the player did not point, and reporting it as an error would call a
 * perfectly normal "not yet" a failure.
 */
export function decideSeekToTick(extent: WrittenExtent, tick: number): SeekDecision {
  const window = seekableWindow(extent);
  const depicted = trackSpanTicks(extent);
  if (window === null || depicted === null) {
    return refuse(seekRefusalFor(extent) ?? 'run-in-flight');
  }
  const onTrack = Math.min(
    depicted.to,
    Math.max(depicted.from, Math.round(finiteOr(tick, depicted.from))),
  );
  if (onTrack > window.to) {
    return refuse('beyond-written-extent');
  }
  return { kind: 'seek', tick: Math.max(window.from, onTrack) };
}

function refuse(reason: SeekRefusalReason): SeekDecision {
  return { kind: 'refused', reason, message: SEEK_REFUSAL_MESSAGES[reason] };
}
